import { useMemo } from 'react'

import { sortFilterOptionValues } from 'features/charts-v2/utils/sort-filter-option-values'
import type { FilterOption, FilterOptionSelect } from 'features/filters/types'
import { applyFilterSelectionToCriteria } from 'features/trend-detection/features/agent-create/utils/agent-filter-criteria'
import type {
  FilterCriterion,
  StaleByField,
} from 'features/trend-detection/types'
import type { BenchmarkingEntityType } from 'src/components/agent-forms/BenchmarkingAgentConfigForm'

export interface FocusEntityOption {
  value: string
  label: string
}

export interface UseFocusEntitySelectionParams {
  criteria: FilterCriterion[]
  onCriteriaChange: (criteria: FilterCriterion[]) => void
  /** The entity dimension the Focus card is currently scoped to. `null`
   * while no entity type is chosen yet. */
  entityField: BenchmarkingEntityType | null
  /** Requested with `fields: [entityType]` by the caller's query — see
   * `useBenchmarkingSetup`'s `focusGroupFilterOptions`.
   *
   * NOT already scoped to just that field, though: the backend's per-field
   * response stubs every unrequested field too (a `{field, alias}` entry
   * with no `values`), so the response still carries every dimension in the
   * project. This hook does the actual narrowing via `entityField`. */
  filterOptions: FilterOption[] | undefined
  filterOptionsLoading: boolean
  staleByField?: StaleByField
}

export interface UseFocusEntitySelectionResult {
  /** Selected entity values for `entityField`, in criterion order. */
  values: string[]
  /** Every value the universe offers for `entityField`, minus `values`,
   * sorted most-mentioned first (falls back to alphabetical when counts
   * aren't loaded — see `sortFilterOptionValues`). */
  availableOptions: FocusEntityOption[]
  isLoading: boolean
  addValue: (value: string) => void
  removeValue: (value: string) => void
  /** Selected values the universe no longer offers — the customer-edit
   * surface's stale-filter signal on the focus side. */
  staleValues: string[]
}

/**
 * Adapts the focus-group filter list into a plain `string[]` selection for
 * `FocusEntityPicker` + `BenchmarkFocusCard`'s value chiclets. Replaces
 * `useFocusGroupFilterPanel` — focus is brands/products ONLY (decision 1 of
 * the Universe/Focus rework), so it collapses to at most one
 * `FilterCriterion` (`{field: entityField, values}`) rather than the full
 * `FilterControlProps` machinery the old focus picker needed to drive
 * `FilterSelectionPopover`.
 *
 * Writes always key by `entityField` itself (never a raw `FilterOption.field`
 * that might come back as the `product`/`product_hierarchy` alias) — since
 * there is no popover in between choosing the write-path field for us, this
 * hook owns that choice and keeps it canonical.
 */
export function useFocusEntitySelection({
  criteria,
  onCriteriaChange,
  entityField,
  filterOptions,
  filterOptionsLoading,
  staleByField,
}: UseFocusEntitySelectionParams): UseFocusEntitySelectionResult {
  // Narrows the (not-yet-scoped) filterOptions response down to the single
  // entityField — see this param's doc comment for why the response alone
  // isn't already narrowed. The API can return products as either `product`
  // or `product_hierarchy` (mirrors the existing normalization in
  // cohort-focus-overlap.ts).
  const entityOption = useMemo<FilterOptionSelect | undefined>(() => {
    if (!entityField) return undefined
    return (filterOptions ?? []).find((option) => {
      const normalized =
        option.field === 'product' ? 'product_hierarchy' : option.field
      return normalized === entityField && 'values' in option
    }) as FilterOptionSelect | undefined
  }, [filterOptions, entityField])

  const values = useMemo(
    () => criteria.find((c) => c.field === entityField)?.values ?? [],
    [criteria, entityField],
  )

  const availableOptions = useMemo<FocusEntityOption[]>(() => {
    const optionValues = entityOption?.values ?? []
    const selected = new Set(values)
    const remaining = optionValues.filter((v) => !selected.has(v.value))
    return sortFilterOptionValues(remaining).map((v) => ({
      value: v.value,
      label: v.value,
    }))
  }, [entityOption, values])

  const writeValues = (nextValues: string[]): void => {
    if (!entityField) return
    onCriteriaChange(
      applyFilterSelectionToCriteria(criteria, entityField, nextValues),
    )
  }

  const addValue = (value: string): void => {
    if (values.includes(value)) return
    writeValues([...values, value])
  }

  const removeValue = (value: string): void => {
    writeValues(values.filter((v) => v !== value))
  }

  const staleValues = useMemo(() => {
    if (!entityField || !staleByField) return []
    const stale = staleByField[entityField]?.values ?? []
    return stale.filter((v) => values.includes(v))
  }, [entityField, staleByField, values])

  return {
    values,
    availableOptions,
    isLoading: filterOptionsLoading,
    addValue,
    removeValue,
    staleValues,
  }
}
